/**
 * Auth Token Composable
 * Token 管理（登入狀態）
 *
 * 架構：
 * - 單例模式：使用 nuxtApp._authTokenContext 避免 computed 重複創建
 */

import { computed } from 'vue'
import { useCookie, useRuntimeConfig, useNuxtApp } from '#app'

const TOKEN_SECONDS = 60 * 60 * 24

export const useAuthToken = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 useCookie 和 computed 只創建一次
  if (!nuxtApp._authTokenContext) {
    const { clientId } = useRuntimeConfig().public
    const isProduction = import.meta.env.MODE === 'production'

    const tokenA = useCookie(`cuora_${clientId}-a`, {
      default: () => null,
      maxAge: TOKEN_SECONDS,
      secure: isProduction,
      sameSite: 'lax',
    })

    const tokenB = useCookie(`cuora_${clientId}-b`, {
      default: () => null,
      maxAge: TOKEN_SECONDS,
      secure: isProduction,
      sameSite: 'lax',
    })

    // computed 只創建一次
    const token = computed(() => {
      return tokenA.value || tokenB.value
    })

    const isLoggedIn = computed(() => {
      return !!token.value
    })

    const isGuest = computed(() => {
      return !token.value
    })

    const setToken = (newToken) => {
      tokenA.value = newToken
    }

    const extendToken = () => {
      if (tokenA.value) {
        tokenB.value = tokenA.value
        tokenA.value = null
      } else {
        tokenA.value = tokenB.value
        tokenB.value = null
      }
    }

    const clearToken = () => {
      tokenA.value = null
      tokenB.value = null
    }

    const getAuthHeaders = () => {
      return token.value
        ? {
            Authorization: `Bearer ${token.value}`,
          }
        : {}
    }

    // cleanup 方法：供 SSR cleanup plugin 調用（此 composable 無 watch 需清理）
    const cleanup = () => {}

    nuxtApp._authTokenContext = {
      token,
      isLoggedIn,
      isGuest,
      setToken,
      clearToken,
      extendToken,
      getAuthHeaders,
      cleanup,
    }
  }

  return nuxtApp._authTokenContext
}
